import { useEffect, useRef, useState } from 'react';
import { ChartPermission, ErrorCard, ErrorFormField } from 'components/common';
import { ELSButton, ELSFlex, ELSFlexItem, ELSIcon } from 'components/common/els';
import { ChartViewProps } from 'components/features/shared/constants';
import { FormFieldInputType } from 'models/enum';
import { ChartMetaFormField } from 'models/ui';
import { AdvanceDirectiveFieldConfig, FIELD_CONFIG_MAP, FormField, hasFieldValue, STATIC_TEXT, SectionTitle, STEP_CONFIGS, ViewTitle } from './constants';
import './advance.directive.scss';

export interface AdvanceDirectiveViewProps extends ChartViewProps {
  activeStep: number;
  canContinue: boolean;
  canReset?: boolean;
  completedSteps?: boolean[];
  displayAuthoringData?: Function;
  enableDisplayRecordsButton?: boolean;
  furthestStep: number;
  isAuthor: boolean;
  isDemoView?: boolean;
  isReadOnly: boolean;
  onBackStep: VoidFunction;
  onContinueStep: VoidFunction;
  onResetStep: VoidFunction;
  onSelectStep: (stepIndex: number) => void;
}

const getFieldConfig = (fieldName: FormField): AdvanceDirectiveFieldConfig | undefined => FIELD_CONFIG_MAP.get(fieldName);

const getTrimmedChangeEvent = (arg: unknown, maxLength: number) => {
  const nextValue = String((arg as { target?: { value?: unknown } })?.target?.value ?? '');
  if (nextValue.length <= maxLength) return arg;

  return {
    ...(arg as Record<string, unknown>),
    target: {
      ...((arg as { target?: Record<string, unknown> })?.target ?? {}),
      value: nextValue.slice(0, maxLength)
    }
  };
};

const getRenderedField = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField, isReadOnly: boolean): ChartMetaFormField => {
  const formField = formFieldMap.get(fieldName);

  if (!formField) return formField;

  const fieldConfig = getFieldConfig(fieldName);
  // The required asterisk disappears once the field has a value — mirroring Consent for Blood Transfusion.
  const isRequired = Boolean(fieldConfig?.isRequired) && !hasFieldValue(formField);
  const withDisabled = isReadOnly ? { ...formField, disabled: true, isRequired } : { ...formField, isRequired };
  if (typeof fieldConfig?.maxLength === 'number' && withDisabled.onChange) {
    return {
      ...withDisabled,
      onChange: (field: ChartMetaFormField, arg: unknown, ...rest: unknown[]) =>
        withDisabled.onChange(field, getTrimmedChangeEvent(arg, fieldConfig.maxLength), ...(rest as [unknown]))
    };
  }

  return withDisabled;
};

// The "Statement of Desires" step must be read before the user can agree or move on.
const STATEMENT_STEP_INDEX = 1;
const SCROLL_END_THRESHOLD = 4;

const AdvanceDirectiveView = (props: AdvanceDirectiveViewProps) => {
  const {
    activeStep,
    canContinue,
    canReset,
    completedSteps = [],
    displayAuthoringData,
    enableDisplayRecordsButton,
    formFieldMap,
    formSubmittedCount,
    furthestStep,
    isAuthor,
    isDemoView,
    isReadOnly,
    onBackStep,
    onContinueStep,
    onResetStep,
    onSelectStep
  } = props;
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === STEP_CONFIGS.length - 1;

  const statementScrollRef = useRef<HTMLDivElement>(null);
  const [statementRead, setStatementRead] = useState(false);
  const requiresStatementScroll = activeStep === STATEMENT_STEP_INDEX && !isReadOnly;

  const markStatementReadIfAtEnd = (element: HTMLDivElement | null) => {
    if (!element) return;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - SCROLL_END_THRESHOLD) {
      setStatementRead(true);
    }
  };

  useEffect(() => {
    if (activeStep !== STATEMENT_STEP_INDEX) return;
    // Reset the gate on entry, then unlock immediately when the content is short enough to fit.
    setStatementRead(false);
    const element = statementScrollRef.current;
    if (element && element.scrollHeight <= element.clientHeight + SCROLL_END_THRESHOLD) {
      setStatementRead(true);
    }
  }, [activeStep]);

  const isStatementGateBlocking = requiresStatementScroll && !statementRead;

  const renderField = (fieldName: FormField, extraClassName?: string) => {
    const formField = getRenderedField(formFieldMap, fieldName, isReadOnly);
    const fieldConfig = getFieldConfig(fieldName);
    const signatureClass = fieldConfig?.isSignature ? ' advance-directive__signature' : '';
    const inputProps = fieldConfig?.placeholder ? { placeholder: fieldConfig.placeholder } : undefined;
    const isTextArea = fieldConfig?.type === FormFieldInputType.TEXT_AREA;

    return (
      <div className={`advance-directive__field${signatureClass}${extraClassName ? ` ${extraClassName}` : ''}`}>
        <ErrorFormField formField={formField} formSubmittedCount={formSubmittedCount} formFieldMap={formFieldMap} inputProps={inputProps} useDefaultMargin={!isTextArea} />
        {isTextArea && fieldConfig?.maxLength && (
          <div className="advance-directive__char-count">{`${String(formField?.value ?? '').length}/${fieldConfig.maxLength} characters`}</div>
        )}
      </div>
    );
  };

  const renderNameAddressGroup = (nameField: FormField, addressField: FormField, thirdField: FormField, thirdFieldLast: boolean) => (
    <ELSFlex className="u-flex-gutter-3x" left wrap>
      <ELSFlexItem md="1o1" w="1o2">
        {renderField(nameField)}
        {thirdFieldLast && renderField(thirdField)}
      </ELSFlexItem>
      <ELSFlexItem md="1o1" w="1o2">
        {renderField(addressField)}
      </ELSFlexItem>
      {!thirdFieldLast && (
        <ELSFlexItem md="1o1" w="1o2">
          {renderField(thirdField)}
        </ELSFlexItem>
      )}
    </ELSFlex>
  );

  const renderStaticList = (items: string[]) => (
    <ol className="advance-directive__legal-list">
      {items.map((item, itemIndex) => (
        <li key={itemIndex}>
          {item.split('\n').map((line, lineIndex) => (
            <p key={`${itemIndex}-${lineIndex}`}>{line}</p>
          ))}
        </li>
      ))}
    </ol>
  );

  const renderStepContent = (stepIndex: number) => {
    if (stepIndex === 0) {
      return (
        <>
          <h4 className="advance-directive__subsection-title">{STATIC_TEXT.AGENT_SECTION_TITLE}</h4>
          <p className="advance-directive__legal-text">{STATIC_TEXT.AGENT_SECTION_INTRO}</p>
          {renderNameAddressGroup(FormField.AGENT_NAME, FormField.AGENT_ADDRESS, FormField.AGENT_PHONE_NUMBER, true)}

          <h4 className="advance-directive__subsection-title u-els-margin-top-2x">{STATIC_TEXT.ALT_AGENT_SECTION_TITLE}</h4>
          <p className="advance-directive__legal-text">{STATIC_TEXT.ALT_AGENT_SECTION_INTRO}</p>
          {renderNameAddressGroup(FormField.ALT_AGENT_NAME, FormField.ALT_AGENT_ADDRESS, FormField.ALT_AGENT_PHONE_NUMBER, true)}
        </>
      );
    }

    if (stepIndex === 1) {
      return (
        <div
          className="advance-directive__scroll-region"
          ref={statementScrollRef}
          tabIndex={0}
          role="region"
          aria-label={SectionTitle.STATEMENT_OF_DESIRES}
          onScroll={(event) => markStatementReadIfAtEnd(event.currentTarget)}
        >
          <h4 className="advance-directive__subsection-title">{STATIC_TEXT.GENERAL_INTENT_TITLE}</h4>
          {renderStaticList(STATIC_TEXT.GENERAL_INTENT_ITEMS)}

          <h4 className="advance-directive__subsection-title u-els-margin-top-2x">{STATIC_TEXT.LIFE_SUSTAINING_TITLE}</h4>
          <p className="advance-directive__legal-text">{STATIC_TEXT.LIFE_SUSTAINING_INTRO}</p>
          {renderStaticList(STATIC_TEXT.LIFE_SUSTAINING_ITEMS)}
        </div>
      );
    }

    if (stepIndex === 2) {
      return (
        <>
          <p className="advance-directive__legal-text">{STATIC_TEXT.DECLARATION_OF_UNDERSTANDING_INTRO}</p>
          <ELSFlex className="u-flex-gutter-3x" left wrap>
            <ELSFlexItem md="1o1" w="1o2">
              {renderField(FormField.DECLARATION_NAME_PRINT)}
            </ELSFlexItem>
            <ELSFlexItem md="1o1" w="1o2">
              {renderField(FormField.DECLARATION_SIGNATURE)}
            </ELSFlexItem>
          </ELSFlex>
        </>
      );
    }

    return (
      <>
        <p className="advance-directive__legal-text">{STATIC_TEXT.WITNESSES_INTRO_1}</p>
        <p className="advance-directive__legal-text">{STATIC_TEXT.WITNESSES_INTRO_2}</p>
        {[
          { title: 'Witness 1', name: FormField.WITNESS_1_NAME, address: FormField.WITNESS_1_ADDRESS, signature: FormField.WITNESS_1_SIGNATURE },
          { title: 'Witness 2', name: FormField.WITNESS_2_NAME, address: FormField.WITNESS_2_ADDRESS, signature: FormField.WITNESS_2_SIGNATURE },
          { title: 'Witness 3', name: FormField.WITNESS_3_NAME, address: FormField.WITNESS_3_ADDRESS, signature: FormField.WITNESS_3_SIGNATURE }
        ].map((witness) => (
          <div className="advance-directive__witness" key={witness.title}>
            <h4 className="advance-directive__subsection-title">{witness.title}</h4>
            {renderNameAddressGroup(witness.name, witness.address, witness.signature, true)}
          </div>
        ))}
      </>
    );
  };

  const getContinueText = (stepIndex: number) => {
    if (isAuthor) return stepIndex === STEP_CONFIGS.length - 1 ? 'Save Records' : 'Save & Next';
    if (stepIndex === 1) return 'I Agree';
    return stepIndex === STEP_CONFIGS.length - 1 ? 'Save & Complete' : 'Continue';
  };

  const showResetButton = !isReadOnly && STEP_CONFIGS[activeStep].fields.length > 0 && !isLastStep;

  return (
    <div className="advance-directive">
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />
      <h1>{ViewTitle.ADVANCE_DIRECTIVE}</h1>

      <div className="advance-directive__self-declaration">
        <h3 className="advance-directive__section-title">{SectionTitle.SELF_DECLARATION}</h3>
        {renderField(FormField.DECLARANT_NAME, 'advance-directive__declarant')}
      </div>

      <div className="advance-directive__intro">
        <h3 className="advance-directive__section-title">{STATIC_TEXT.PHYSICAL_HEALTH_CARE_TITLE}</h3>
        <p className="advance-directive__legal-text">{STATIC_TEXT.PHYSICAL_HEALTH_CARE_INTRO}</p>
      </div>

      <div className="advance-directive__flow">
        {STEP_CONFIGS.map((stepConfig, stepIndex) => {
          const isActive = stepIndex === activeStep;
          const canAccessStep = stepIndex <= furthestStep;
          const isCompleted = Boolean(completedSteps[stepIndex]);

          return (
            <section className="advance-directive__step" key={stepConfig.title}>
              <button
                className={`advance-directive__step-header ${canAccessStep ? 'advance-directive__step-header--clickable' : 'advance-directive__step-header--locked'}`}
                disabled={!canAccessStep}
                onClick={() => onSelectStep(stepIndex)}
                type="button"
              >
                <span
                  className={[
                    'advance-directive__step-index',
                    isActive && 'advance-directive__step-index--active',
                    isCompleted && !isActive && 'advance-directive__step-index--completed'
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {isCompleted && !isActive ? <ELSIcon name="checkmark" size="1x" color="white" title="Completed" /> : stepIndex + 1}
                </span>
                <span className={`advance-directive__step-title${isActive ? ' advance-directive__step-title--active' : ''}`}>{stepConfig.title}</span>
              </button>

              {isActive && (
                <div className="advance-directive__panel">
                  {renderStepContent(stepIndex)}

                  <ChartPermission>
                    {({ allowSaveFragment }) => (
                      <div className="advance-directive__actions">
                        {!isFirstStep && (
                          <ELSButton
                            isDisabled={isStatementGateBlocking}
                            onClick={onBackStep}
                            text="Back"
                            type="secondary"
                            iconName="chevron-left"
                            iconPosition="left"
                            iconSize="1x"
                          />
                        )}

                        {isLastStep ? (
                          !isReadOnly && (
                            <>
                              {!isDemoView && (
                                <ELSButton
                                  isDisabled={!canContinue || (isAuthor && !allowSaveFragment)}
                                  onClick={onContinueStep}
                                  text={getContinueText(stepIndex)}
                                  iconName={isAuthor ? undefined : 'chevron-right'}
                                  iconPosition={isAuthor ? undefined : 'right'}
                                  iconSize="1x"
                                />
                              )}
                              {isAuthor && (
                                <ELSButton type="secondary" text="Return with data" isDisabled={!enableDisplayRecordsButton} onClick={displayAuthoringData} />
                              )}
                            </>
                          )
                        ) : (
                          <ELSButton
                            isDisabled={!canContinue || isStatementGateBlocking}
                            onClick={onContinueStep}
                            text={getContinueText(stepIndex)}
                            type="primary"
                            iconName="chevron-right"
                            iconPosition="right"
                            iconSize="1x"
                          />
                        )}

                        {showResetButton && <ELSButton onClick={onResetStep} text="Reset" type="secondary" isDisabled={!canReset} />}
                      </div>
                    )}
                  </ChartPermission>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

AdvanceDirectiveView.displayName = 'AdvanceDirectiveView';
export default AdvanceDirectiveView;
